import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// Image assets designed for kitchen inspiration
const kitchenImages = {
  hero: "https://images.unsplash.com/photo-1556911220-e15024d8693c?auto=format&fit=crop&w=1200&q=80",
  venetian: "https://images.unsplash.com/photo-1560185007-cde436f6a4d0?auto=format&fit=crop&w=600&q=80",
  pvc: "https://images.unsplash.com/photo-1556909172-54557c7e4fa7?auto=format&fit=crop&w=600&q=80",
};

function KitchenBlinds() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 text-slate-900">
      <Helmet>
        <title>Kitchen Blinds UK | Waterproof & Easy-Clean Window Blinds</title>
        <meta
          name="description"
          content="Transform your kitchen with made-to-measure blinds. Shop 100% waterproof rollers, faux wood Venetians, and fire-retardant fabrics. Durable and stylish for 2026 UK homes."
        />
        <meta name="keywords" content="Kitchen Blinds UK, Waterproof Kitchen Blinds, Faux Wood Venetians, Easy Clean Blinds, Fire Retardant Blinds, Kitchen Window Treatments" />
      </Helmet>

      {/* --- Hero Section --- */}
      <section className="grid lg:grid-cols-2 gap-12 items-center mb-24">
        <div>
          <span className="bg-amber-100 text-amber-800 px-4 py-1 rounded-full text-sm font-bold tracking-wide uppercase">
            Practical & Stylish
          </span>
          <h1 className="text-5xl md:text-6xl font-black mt-4 mb-6 leading-tight">
            Kitchen <br />
            <span className="text-amber-600">Blinds</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            The kitchen is more than just a place to cook—it's a hub for socializing and family. But with <strong>steam, grease, and heat</strong>, your window treatments need to be as tough as they are beautiful.
          </p>
          <div className="flex flex-wrap gap-4">
            <button className="bg-slate-900 text-white px-8 py-4 rounded-xl font-bold hover:bg-amber-600 transition-all shadow-lg">
              Shop Kitchen Range
            </button>
            <button className="bg-white border-2 border-slate-200 px-8 py-4 rounded-xl font-bold hover:bg-slate-50 transition">
              Order Free Samples
            </button>
          </div>
        </div>
        <div className="relative">
          <img 
            src={kitchenImages.hero} 
            alt="Modern kitchen with large windows and clean white blinds" 
            className="rounded-3xl shadow-2xl h-[500px] w-full object-cover"
          />
        </div>
      </section>

      {/* --- Feature Highlight: The Wipe-Clean Factor --- */}
      <section className="bg-slate-50 rounded-[2.5rem] p-10 md:p-16 mb-24">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Engineered for Every Spill</h2>
          <p className="text-slate-600 italic">"Because life happens in the kitchen."</p>
        </div>
        <div className="grid md:grid-cols-3 gap-12 text-center">
          <div>
            <div className="text-4xl mb-4">💧</div>
            <h3 className="text-xl font-bold mb-2">100% Waterproof</h3>
            <p className="text-slate-600">Our high-grade PVC rollers are immune to steam and splashes from the sink.</p>
          </div>
          <div>
            <div className="text-4xl mb-4">🧽</div>
            <h3 className="text-xl font-bold mb-2">Wipe-Clean Tech</h3>
            <p className="text-slate-600">Easily remove cooking fumes and grease with a simple damp cloth.</p>
          </div>
          <div>
            <div className="text-4xl mb-4">🔥</div>
            <h3 className="text-xl font-bold mb-2">Fire Retardant</h3>
            <p className="text-slate-600">Safety first. Many of our kitchen fabrics are treated for fire resistance.</p>
          </div>
        </div>
      </section>

      {/* --- Style Comparison --- */}
      <section className="mb-24">
        <h2 className="text-3xl font-bold mb-12">The Best Styles for Kitchen Windows</h2>
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
              <div>
                <h4 className="text-xl font-bold">Faux Wood Venetian Blinds</h4>
                <p className="text-slate-600">The aesthetic of real wood with the durability of PVC. These won't warp in humid conditions, making them perfect for over the kitchen sink.</p>
                <Link to="/wooden-blinds" className="text-amber-600 font-bold hover:underline mt-2 inline-block">Shop Faux Wood →</Link>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
              <div>
                <h4 className="text-xl font-bold">Moisture-Resistant Rollers</h4>
                <p className="text-slate-600">Available in hundreds of patterns and vibrant colors to match your kitchen decor. Our PVC collection is totally waterproof.</p>
                <Link to="/roller-blinds" className="text-amber-600 font-bold hover:underline mt-2 inline-block">Shop Rollers →</Link>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
              <div>
                <h4 className="text-xl font-bold">Perfect Fit Blinds</h4>
                <p className="text-slate-600">No drilling required. These fit directly onto your uPVC kitchen door or window, keeping your window sills clutter-free.</p>
                <Link to="/perfect-fit-blinds" className="text-amber-600 font-bold hover:underline mt-2 inline-block">Shop Perfect Fit →</Link>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
             <img src={kitchenImages.venetian} alt="Kitchen venetian blinds" className="rounded-2xl shadow-md h-64 w-full object-cover" />
             <img src={kitchenImages.pvc} alt="PVC roller material" className="rounded-2xl shadow-md h-64 w-full object-cover mt-8" />
          </div>
        </div>
      </section>

      {/* --- SEO FAQ Section --- */}
      <section className="max-w-4xl mx-auto mb-24">
        <h2 className="text-3xl font-bold mb-8 text-center">Kitchen Blinds Advice</h2>
        <div className="space-y-6">
          <div className="p-6 border border-slate-200 rounded-2xl">
            <h4 className="font-bold text-lg mb-2">Which is better for a kitchen: Wood or Faux Wood?</h4>
            <p className="text-slate-700">In a kitchen, <strong>Faux Wood</strong> is always the winner. Real wood can absorb moisture and steam, leading to warping and cracking over time. Faux wood (PVC) gives you the same look but is 100% waterproof.</p>
          </div>
          <div className="p-6 border border-slate-200 rounded-2xl">
            <h4 className="font-bold text-lg mb-2">Can I use motorized blinds in the kitchen?</h4>
            <p className="text-slate-700">Yes! In 2026, <strong>Smart Kitchens</strong> are a huge trend. Motorized blinds are ideal if your kitchen window is hard to reach—like behind a large island or deep sink.</p>
          </div>
        </div>
      </section>

      {/* --- Footer & Internal Links --- */}
      <footer className="bg-slate-900 text-white p-12 rounded-[2.5rem] text-center">
        <h2 className="text-3xl font-bold mb-4">Complete Your Interior</h2>
        <p className="mb-8 opacity-70">Looking for other moisture-resistant solutions?</p>
        <div className="flex flex-wrap justify-center gap-6 mb-12">
          <Link to="/bathroom-blinds" className="text-white border-b border-amber-500 hover:text-amber-500 transition">Bathroom Blinds</Link>
          <Link to="/roller-blinds" className="text-white border-b border-amber-500 hover:text-amber-500 transition">PVC Roller Blinds</Link>
          <Link to="/perfect-fit-blinds" className="text-white border-b border-amber-500 hover:text-amber-500 transition">No-Drill Blinds</Link>
        </div>
        <div className="bg-amber-600 p-8 rounded-2xl inline-block shadow-2xl">
          <p className="text-xl font-bold mb-4">Not sure which color to choose?</p>
          <button className="bg-white text-slate-900 px-8 py-3 rounded-full font-bold hover:scale-105 transition-transform">
            Get 10 Free Samples Today
          </button>
        </div>
      </footer>
    </div>
  );
}

export default KitchenBlinds;